import {
  useCreate,
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";
import { z } from "zod";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { ShelvesEditFormType } from "@/pages/shelves/schemas/shelves.edit.form";

export const useShelfRepository = () => {
  const { getOne, getList } = useDataProvider()();
  const { mutateAsync: updateAsync } = useUpdate({ resource: "shelves" });
  const { mutateAsync: createAsync } = useCreate({ resource: "shelves" });
  const { open } = useNotification();
  const { edit } = useNavigation();

  async function get(id: string): Promise<any> {
    const response = await getOne({ id, resource: "shelves" });
    return response;
  }

  async function create(values: ShelvesEditFormType) {
    const response = await createAsync?.(
      {
        values,
      },
      {
        onSuccess: (data) => {
          console.debug("Successful shelf creation", data);
          edit("shelves", (data as any)?.id?.toString()!, "replace");
          open?.({
            type: "success",
            message: "Successfully created shelf",
            description: `You created a shelf called ${values.name}`,
          });
        },
        onError: (error) => {
          console.error("Failed to create shelf with values", values, error);
          if (error instanceof z.ZodError) {
            open?.({
              type: "error",
              message: "Create shelf request failed due to step validation",
              description: toHumanErrorMessage(error),
            });
          } else {
            open?.({
              type: "error",
              message: "Create shelf request failed",
              description: toHumanErrorMessage(error),
            });
          }
        },
      },
    );
  }

  async function update(values: ShelvesEditFormType) {
    const response = await updateAsync?.(
      {
        id: values.id!,
        values,
      },
      {
        onSuccess: (data) => {
          console.debug("Successful shelf update", data);
          edit("shelves", values.id!.toString(), "replace");
          open?.({
            type: "success",
            message: "Successfully updated shelf",
            description: `You updated a shelf called ${values.name}`,
          });
        },
        onError: (error) => {
          console.error("Failed to update shelf with values", values, error);
          if (error instanceof z.ZodError) {
            open?.({
              type: "error",
              message: "Update shelf request failed due to step validation",
              description: toHumanErrorMessage(error),
            });
          } else {
            open?.({
              type: "error",
              message: "Update shelf request failed",
              description: toHumanErrorMessage(error),
            });
          }
        },
      },
    );

    return response.data.id?.toString()!;
  }

  return { get, update, create };
};
